import { day } from './factory'
import type { InboxBooking, InboxLead, InboxOrder } from '../../core/types/adminRequests'

/**
 * DEMO — voorbeeldberichten voor het beheer. demo/seed/build.ts voegt dit samen met
 * de klanten tot losse tabellen: een bestelling die hier én in een klantaccount
 * staat, wordt één rij. Afspraakaanvragen van klanten komen uit hun dossier.
 */

const at = (offset: number, time: string) => `${day(offset)}T${time}`

/** Zoals de server een bestelling bewaart; prijzen en verzendkosten legt demo/pricing.ts vast. */
export type SeedOrder = Omit<InboxOrder, 'totalCents'>

const paid = { status: 'betaald', method: 'ideal' } as const

export const seedOrders: SeedOrder[] = [
  {
    // Automatische abonnementsbetaling die mislukte (F20). Staat onderaan de inbox en kan niet
    // ingepakt worden; Eva ziet in Mijn omgeving "Opnieuw betalen".
    id: 'BB-2026-0436',
    receivedAt: at(0, '06:00'),
    customerId: 'klant-2208',
    name: 'Eva Jansen',
    city: 'Haarlem',
    status: 'nieuw',
    payment: { status: 'mislukt', method: 'ideal' },
    gift: null,
    order: {
      email: 'eva@voorbeeld.nl',
      address: { firstName: 'Eva', lastName: 'Jansen', street: 'Kruisweg', houseNumber: '64', postcode: '2011 LE', city: 'Haarlem', country: 'NL' },
      lines: [
        { productId: 'emerginc-peach-toner', qty: 1, subscribe: true },
        { productId: 'emerginc-kombucha-cleanser', qty: 1, subscribe: true },
      ],
      code: null,
      skinNote: '',
      refillReminder: false,
    },
  },
  {
    id: 'BB-2026-0420',
    receivedAt: at(0, '09:14'),
    customerId: 'klant-2208',
    name: 'Eva Jansen',
    city: 'Haarlem',
    status: 'nieuw',
    payment: paid,
    gift: null,
    order: {
      email: 'eva@voorbeeld.nl',
      address: { firstName: 'Eva', lastName: 'Jansen', street: 'Kruisweg', houseNumber: '64', postcode: '2011 LE', city: 'Haarlem', country: 'NL' },
      lines: [
        { productId: 'emerginc-peach-toner', qty: 2, subscribe: true },
        { productId: 'emerginc-kombucha-cleanser', qty: 1, subscribe: true },
      ],
      code: null,
      skinNote: 'Ik ben 12 weken zwanger — kan ik deze gewoon blijven gebruiken?',
      refillReminder: true,
    },
  },
  {
    id: 'BB-2026-0421',
    receivedAt: at(-1, '21:40'),
    name: 'Merel de Wit',
    city: 'Antwerpen',
    status: 'nieuw',
    // Voorbeeld van een afgebroken Mollie-betaling: staat onderaan en kan niet ingepakt worden.
    payment: { status: 'open', method: 'card' },
    gift: null,
    order: {
      email: 'merel@voorbeeld.nl',
      address: { firstName: 'Merel', lastName: 'de Wit', street: 'Kammenstraat', houseNumber: '17', postcode: '2000', city: 'Antwerpen', country: 'BE' },
      lines: [{ productId: 'emerginc-vitamine-c-serum', qty: 1, subscribe: false }],
      code: 'SALON',
      skinNote: '',
      refillReminder: false,
    },
  },
  {
    id: 'BB-2026-0415',
    receivedAt: at(-1, '13:02'),
    customerId: 'klant-1932',
    name: 'Nadia Bakker',
    city: 'Amsterdam',
    status: 'ingepakt',
    payment: paid,
    gift: null,
    order: {
      email: 'nadia@voorbeeld.nl',
      address: { firstName: 'Nadia', lastName: 'Bakker', street: 'Czaar Peterstraat', houseNumber: '20', postcode: '1018 PR', city: 'Amsterdam', country: 'NL' },
      lines: [{ productId: 'medik8-retinoate-bestseller', qty: 1, subscribe: false }],
      code: null,
      skinNote: '',
      refillReminder: true,
    },
  },
  {
    id: 'BB-2026-0412',
    receivedAt: at(-3, '10:27'),
    customerId: 'klant-2291',
    name: 'Lotte de Vries',
    city: 'Amsterdam',
    status: 'verzonden',
    tracking: { carrier: 'PostNL', code: '3SBBEW0412345' },
    payment: paid,
    gift: null,
    order: {
      email: 'lotte@voorbeeld.nl',
      address: { firstName: 'Lotte', lastName: 'de Vries', street: 'Javastraat', houseNumber: '41-2', postcode: '1094 HA', city: 'Amsterdam', country: 'NL' },
      lines: [{ productId: 'vitality-mask', qty: 1, subscribe: false }],
      code: null,
      skinNote: '',
      refillReminder: false,
    },
  },
]

/** Aanvragen van mensen zonder account (via de afsprakenlade). */
export const seedGuestBookings: InboxBooking[] = [
  {
    id: 'AF-118',
    receivedAt: at(0, '08:05'),
    status: 'aanvraag',
    change: 'nieuw',
    booking: {
      treatmentSlug: 'persian-delight',
      date: day(8),
      time: '14:30',
      name: 'Anne Visser',
      email: 'anne@voorbeeld.nl',
      firstVisit: true,
    },
  },
]

export const seedLeads: InboxLead[] = [
  {
    id: 'lead-1',
    receivedAt: at(0, '07:48'),
    lead: {
      email: 'anouk@voorbeeld.nl',
      routineId: 'pigment-protocol',
      answers: [
        { questionId: 'concern', answer: 'Pigmentvlekken' },
        { questionId: 'skin', answer: 'Reageert soms' },
        { questionId: 'routine', answer: 'Cleanser en crème' },
      ],
    },
  },
  {
    id: 'lead-2',
    receivedAt: at(-1, '22:10'),
    lead: {
      email: 'kim@voorbeeld.nl',
      routineId: 'compleet-anti-ageing',
      answers: [
        { questionId: 'concern', answer: 'Eerste lijntjes' },
        { questionId: 'skin', answer: 'Kan veel hebben' },
        { questionId: 'routine', answer: 'Uitgebreide routine' },
      ],
    },
  },
]

export const seedNewsletter = { subscribers: 214, lastWeek: 9 }
